import { apiClient } from '../../../../services/apiClient'
import type {
  Land,
  LandSearchRequest,
  LandSearchResponse,
} from '@popety-io/types'

/**
 * Land service for DeveloperMap feature
 * Contains DeveloperMap-specific land fetching methods
 *
 * For Analysis features (BuildingAnalysis, LandAnalysis),
 * use landService from @/pages/Analysis/Shared instead
 */
export const landService = {
  /**
   * Get a single land by ID (internal helper method)
   * @private
   */
  async getLand(id: string, fields?: string[]): Promise<Land> {
    const query = fields
      ? `?fields=${encodeURIComponent(fields.join(','))}`
      : ''

    const url = `/lands/${id}${query}`

    const response = await apiClient.get(url)

    return response.data
  },

  /**
   * Search for lands with filters (internal helper method)
   * @private
   */
  async searchLands(request: LandSearchRequest): Promise<LandSearchResponse> {
    const response = await apiClient.post('/lands/search', request)
    return response.data
  },

  /**
   * Get land data for property tooltip in DeveloperMap
   * Optimized to return only necessary fields for performance
   */
  async getLandForTooltip(id: string): Promise<Land> {
    const fields = [
      'id',
      'land_id',
      'egrid_nb',
      'address_full',
      'area',
      'official_area',
      'bare_land',
      'ownership_type',
      'principal_type',
      'building_nb',
      'buildings.id',
      'buildings.area',
      'buildings.addresses',
      'buildings.buildings_administrative.egid',
      'buildings.buildings_administrative.building_year',
      'buildings.buildings_administrative.building_period',
      'buildings.buildings_administrative.renovation_year',
      'buildings.buildings_administrative.renovation_period',
      'buildings.buildings_administrative.unit_nb',
      'buildings.buildings_administrative.area',
      'buildings.buildings_administrative.volume',
      'buildings.buildings_administrative.max_height',
      'buildings.buildings_administrative.floor_nb',
      'buildings.buildings_administrative.residential_floor_nb',
      'buildings.buildings_administrative.residential_area',
      'buildings.buildings_administrative.heating_energy_source',
      'buildings.buildings_administrative.idc',
      'buildings.buildings_administrative.solar_potential',
      'buildings.buildings_administrative.protected',
      'owners.id',
      'owners.name',
      'owners.type',
      'owner_last_change_date',
      'land_use_plans.name',
      'land_use_plans.plan_name',
      'land_use_plans.plan_type',
      'land_use_plans.plan_date',
      'land_use_plans.building_area',
      'land_use_plans.cover_area_perc',
      'land_use_plans.authorized_cos',
      'land_use_plans.authorized_cus',
      'land_use_plans.authorized_ibus',
      'rdppfs.name',
      'rdppfs.cover_area_perc',
      'rdppfs.rdppf_type_id',
      'rdppfs.active',
      'transactions',
      'listings',
      'construction_permits',
      'geo_shape_score',
      'development_score',
      'livability_score',
      'under_exploited_score',
      'geo_center',
      'existing_score',
      'geo_polygon',
      'municipality_name',
      'region_name',
      'district_name',
      'address_postal_code_name',
      'plot_number',
      'egrid_nb',
      'code_number',
      'main_lupa_cover_area',
      'main_lupa_type',
      'main_lupa_name',
      'unit_total_area',
      'unit_nb',
      'geo_portal_link',
      'rdppf_land_link',
      'region_id',
      'rf_uri',
    ]

    return this.getLand(id, fields)
  },

  /**
   * Search lands by geographic bounds (DeveloperMap feature)
   */
  async searchLandsByBounds(
    bounds: { north: number; south: number; east: number; west: number },
    limit = 100
  ): Promise<LandSearchResponse> {
    const request: LandSearchRequest = {
      filters: { geo_bounds: bounds },
      limit,
      fields: [
        'id',
        'geo_center',
        'geo_polygon',
        'address_full',
        'area',
        'building_nb',
        'development_score',
      ],
    }

    return this.searchLands(request)
  },
}
